import React, { useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Dropdown, Select, Table, Tag } from 'antd';

import ModalActions from 'components/ModalActions';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import Truncate from 'v2/components/Shared/TruncateText';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import { useChangeStatusConnect } from 'v2/hooks/useChangeStatusConnect';
import { useListAutoresponders } from 'v2/hooks/useListAutoresponders';
import { useListMyConnects } from 'v2/hooks/useListMyConnects';

import NewConnect from '../components/NewConnect';
import { useConnectState } from '../contexts/ConnectStateContext';

const RDSTATION = 18,
  RDSTATIONEW = 44,
  GDIGITAL = 43,
  GDIGITALNEW = 46;

const MyConnects: React.FC = () => {
  const { currentConnect, openNewConnectModal, setCurrentConnect } = useConnectState();
  const [paginationParams, setPaginationParams] = useState({ per_page: 25, current_page: 1 });
  const [openModalDisable, setOpenModalDisable] = useState(false);
  const [filter, setFilter] = useState<any>(null);

  const history = useHistory();

  const { myConnects: connects, pagination, isLoading: isFetching, refetch } = useListMyConnects(paginationParams);
  const { autoresponders } = useListAutoresponders();
  const { changeStatusConnect } = useChangeStatusConnect();

  const openModal = useCallback(() => {
    openNewConnectModal();
  }, [openNewConnectModal]);

  const openModalDisableConnect = useCallback(
    (connectItem: any) => {
      setOpenModalDisable(true);
      setCurrentConnect(connectItem);
    },
    [setCurrentConnect]
  );

  const onCancelModal = useCallback(() => {
    setOpenModalDisable(false);
  }, []);

  const changeConnectStatus = useCallback(
    async (status: boolean) => {
      try {
        await changeStatusConnect({ connectId: currentConnect.id, active: status });
        setOpenModalDisable(false);
        toast.success(`Connect ${status ? 'ativado' : 'desativado'} com sucesso!`);
        refetch();
      } catch (error) {
        toast.error(error);
      }
    },
    [currentConnect, changeStatusConnect, refetch]
  );

  const onChangeRowsPerPage = useCallback((perPage: number) => {
    setPaginationParams(prev => ({ ...prev, per_page: perPage, current_page: 1 }));
  }, []);

  const onChangePage = useCallback((page: number) => {
    setPaginationParams(prev => ({ ...prev, current_page: page }));
  }, []);

  const providers = Array.isArray(autoresponders) ? autoresponders : [];
  const listConnects = Array.isArray(connects) ? connects : [];

  const filteredConnects = listConnects.filter((c: any) => {
    if (!filter) {
      return true;
    }

    if (filter === RDSTATION && (c.autoresponder_id === RDSTATION || c.autoresponder_id === RDSTATIONEW)) {
      return true;
    }

    if (filter === GDIGITAL && (c.autoresponder_id === GDIGITAL || c.autoresponder_id === GDIGITALNEW)) {
      return true;
    }

    if (c.autoresponder_id === filter) {
      return true;
    }

    return false;
  });

  const devHubLink =
    'https://ajuda.eduzz.com/hc/pt-br/articles/30130496111771-Como-funciona-o-processo-de-autentica%C3%A7%C3%A3o-com-a-API-Developer-Hub-da-Eduzz';
  const zapierLink = 'https://ajuda.eduzz.com/hc/pt-br/articles/11780815400859-Como-integrar-utilizando-o-Zapier';
  const isAlertProviderDeprecatedEnabled = new Date() < new Date('2025-10-30');

  const getMenuItems = (connectItem: any) => {
    if (connectItem?.migrated) {
      return [
        {
          key: 'edit-new',
          label: 'Editar no Novo Connect',
          onClick: () => history.push('/producer/new-connect')
        }
      ];
    }

    return [
      {
        key: 'toggle-status',
        label: connectItem?.active ? 'Desativar' : 'Ativar',
        onClick: () => openModalDisableConnect(connectItem)
      },
      {
        key: 'view-actions',
        label: 'Ver Ações',
        onClick: () => history.push(`/producer/connect/${connectItem.id}/actions`)
      }
    ];
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'status',
      render: (active: boolean) => <Tag color={active ? 'success' : 'error'}>{active ? 'Ativo' : 'Inativo'}</Tag>
    },
    {
      title: 'Plataforma',
      dataIndex: 'image',
      key: 'platform',
      render: (_: any, record: any) => (
        <div className='flex justify-start max-md:flex-col'>
          <div
            className='mr-3 flex h-8.75 min-w-20 bg-contain bg-center bg-no-repeat'
            style={{ backgroundImage: `url(${record.image})` }}
            title={record.name || 'Desconhecido'}
          />
        </div>
      )
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Dados de configuração',
      key: 'config',
      render: (_: any, record: any) => (
        <>
          <b>{record?.parameters[0]?.label ?? '-'}</b>
          <Truncate count={100} final='...'>
            {record?.parameters[0]?.value}
          </Truncate>
        </>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Dropdown menu={{ items: getMenuItems(record) }} trigger={['click']}>
          <Button type='text' icon={<EllipsisOutlined rotate={90} style={{ fontSize: 24 }} />} />
        </Dropdown>
      )
    }
  ];

  return (
    <>
      <TopPage
        title='Connect'
        action={
          <PermissionHide action={'myeduzz:connect:criar'}>
            <Button type='primary' icon={<PlusOutlined />} onClick={openModal}>
              Nova integração
            </Button>
          </PermissionHide>
        }
      />

      {isAlertProviderDeprecatedEnabled && (
        <Alert
          className='my-2.5'
          type='warning'
          message='Atenção: algumas integrações serão descontinuadas em 30/10'
          description={
            <>
              A partir de 30/10, os provedores ConvertKit, Hotzapp, GDigital, GDigitalNew, InfusionSoft, Aweber,
              MailRelay e Pipz deixarão de estar disponíveis. <br />
              <br /> Recomendamos migrar suas automações para a{' '}
              <a href={devHubLink} target='_blank' rel='noreferrer'>
                Developer Hub
              </a>{' '}
              ou{' '}
              <a href={zapierLink} target='_blank' rel='noreferrer'>
                Zapier
              </a>
              .
            </>
          }
        />
      )}

      <PermissionHide action='owner'>
        <WithBetaModules>
          {(betaModules: string[]) => {
            if (betaModules?.includes('new_connect')) {
              return (
                <Alert
                  className='my-2.5'
                  type='info'
                  message={
                    <>
                      <strong>Uma nova forma de fazer integrações está chegando! </strong>
                      <span>
                        Estamos criando uma nova versão do Connect Eduzz e já migramos alguns dos seus provedores para a
                        nova versão. <Link to='/producer/new-connect'>Bora conferir?</Link>
                      </span>
                    </>
                  }
                />
              );
            }

            return null;
          }}
        </WithBetaModules>
      </PermissionHide>

      <div className='mt-4 flex flex-col gap-4'>
        <div className='flex justify-end'>
          <div className='w-full md:w-1/4'>
            <label className='mb-1 block text-sm'>Plataforma</label>
            <Select
              className='w-full'
              value={filter ?? ''}
              onChange={value => setFilter(value)}
              options={[
                { value: '', label: 'Todas' },
                ...providers
                  .filter((a: any) => a.id !== RDSTATIONEW && a.id !== GDIGITALNEW)
                  .map((option: any) => ({ value: option.id, label: option.name }))
              ]}
            />
          </div>
        </div>

        <div>
          <Table
            columns={columns}
            dataSource={filteredConnects}
            rowKey={(record: any) => `connect-${record.id}`}
            loading={isFetching}
            pagination={{
              total: pagination?.total_items || 0,
              current: pagination?.current_page ?? 1,
              pageSize: pagination?.per_page ?? 25,
              pageSizeOptions: [10, 25, 50],
              onChange: page => onChangePage(page),
              onShowSizeChange: (_, size) => onChangeRowsPerPage(size),
              showSizeChanger: true
            }}
          />
        </div>

        <NewConnect />
        <ModalActions
          open={openModalDisable}
          onClose={onCancelModal}
          onOk={() => changeConnectStatus(!currentConnect.active)}
          title={'Atenção'}
          buttonOk={'Confirmar'}
          loadingColor='#ccc'
        >
          <p>
            Você deseja realmente {currentConnect && currentConnect.active ? 'desativar' : 'ativar'} o connect{' '}
            <b>{currentConnect && currentConnect.name}</b> - {currentConnect?.description} ?{' '}
          </p>
        </ModalActions>
      </div>
    </>
  );
};

export default MyConnects;
